import React, { useState } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Checkbox,
  Flex,
  Menu,
  Modal,
  Paper,
  PaperProps,
  Space,
  Text,
  Tooltip
} from '@mantine/core';
import {
  IconClock,
  IconClockCheck,
  IconDotsVertical,
  IconFileText,
  IconRefresh,
  IconTag,
  IconTrash,
  IconUsersGroup
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FileDropzone } from 'components/FileDropzone';
import { RawResourceTextModal } from 'components/RawResourceTextModal/RawResourceTextModal';
import { ResourceConflictErrorModal } from 'components/ResourceConflictErrorModal';
import { SyncFrequencyEditModal } from 'components/SyncFrequencyEditModal';
import { ItemTagsView } from 'components/TagComponents/ItemTagsView';
import { TagEditModal } from 'components/TagComponents/TagEditModal';
import { TeamAccessModal } from 'components/TeamAccessModal';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useUserContext } from 'contexts/UserContext';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { LanguageService } from 'services/language-service';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { UserService } from 'services/user-service';
import { CRUDOperation } from 'shared/constants/crud-operation';
import { languageToDateFnsLocale } from 'shared/constants/languages';
import { RESOURCE_DELETE_NOTIF_ID } from 'shared/constants/notification-ids';
import { RESOURCES_QUERY_KEY, RESOURCE_SHOPIFY_STORE_QUERY_KEY } from 'shared/constants/query-keys';
import { MY_RESOURCES_FILTER } from 'shared/constants/resource';
import { Resource, ResourceAiProcessingStatus, UnUploadedResource } from 'shared/interfaces/Resource';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { UserAccess } from 'shared/interfaces/User';
import {
  formatMillisecondsToDuration,
  formatResourceName,
  getResourceAiProcessingStatusIndicator,
  getResourceIcon,
  handleResourceOperationError,
  isSyncEnabled,
  isSyncResourceType,
  isUplodedResource
} from 'shared/utils/resource';

interface ResourceTileProps extends PaperProps {
  resource: UnUploadedResource | Resource;
  onResourceClick?: (resource: UnUploadedResource | Resource) => void;
  disabled?: boolean;
  newBadge?: boolean;
  canSelect?: boolean;
  selected?: boolean;
  selectedTeamFilter?: string;
  optionsEnabled?: boolean;
  allTags?: string[];
  fileNameMaxWidth?: number;
}

export function ResourceTile({
  resource,
  onResourceClick,
  disabled = false,
  newBadge = false,
  canSelect = false,
  selected = false,
  selectedTeamFilter = MY_RESOURCES_FILTER,
  optionsEnabled = true,
  allTags = [],
  fileNameMaxWidth,
  ...paperProps
}: ResourceTileProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [loggedInUser] = useUserContext();
  const [subscription] = useSubscriptionContext();

  const [conflictedSmartAssistants, setConflictedSmartAssistants] = useState<SmartAssistant[]>([]);
  const [fileDropzoneOpened, setFileDropzoneOpened] = useState(false);
  const [teamAccessModalOpen, setTeamAccessModalOpen] = useState(false);
  const [editTagModalOpen, setEditTagModalOpen] = useState(false);
  const [syncFrequencyModalOpen, setSyncFrequencyModalOpen] = useState(false);
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [rawTextModalOpened, setRawTextModalOpened] = useState(false);

  const deleteResourceMutation = useMutation({
    mutationFn: ResourceService.deleteResource,
    onMutate: () => NotificationService.startAction(RESOURCE_DELETE_NOTIF_ID, 'FEED_BACK.RESOURCES.LOADING.DELETE'),
    onSuccess: (deletedResourceId: string) => {
      queryClient.setQueryData([RESOURCES_QUERY_KEY, selectedTeamFilter], (existingResources: Resource[] = []) =>
        existingResources.filter((resource) => resource._id !== deletedResourceId)
      );

      // Remove all Shopify Resources queries by partially matching the query key
      queryClient.removeQueries({
        queryKey: [RESOURCE_SHOPIFY_STORE_QUERY_KEY]
      });

      NotificationService.endAction(true, RESOURCE_DELETE_NOTIF_ID, 'FEED_BACK.RESOURCES.SUCCESS.DELETE');
    },
    onError: (error: AxiosError) =>
      handleResourceOperationError(error, CRUDOperation.DELETE, setConflictedSmartAssistants, openErrorModal)
  });

  const syncResourceMutation = useMutation({
    mutationFn: ResourceService.syncResource,
    onSuccess: (processingResource: Resource) => {
      queryClient.setQueryData([RESOURCES_QUERY_KEY, selectedTeamFilter], (existingResources: Resource[] = []) =>
        existingResources.map((resource) => (resource._id === processingResource._id ? processingResource : resource))
      );
    }
  });

  // Open/Close modals
  const closeFileDropzone = () => setFileDropzoneOpened(false);
  const openTeamAccesModal = () => setTeamAccessModalOpen(true);
  const closeTeamAccessModal = () => setTeamAccessModalOpen(false);
  const closeSyncFrequencyModal = () => setSyncFrequencyModalOpen(false);
  const openErrorModal = () => setErrorModalOpened(true);
  const closeErrorModal = () => setErrorModalOpened(false);
  const openEditTagsModal = () => setEditTagModalOpen(true);
  const closeEditTagsModal = () => setEditTagModalOpen(false);
  const openRawTextModal = () => setRawTextModalOpened(true);
  const closeRawTextModal = () => setRawTextModalOpened(false);

  const onErrorModalClose = () => {
    closeErrorModal();
    setConflictedSmartAssistants([]);
  };

  return (
    <>
      <Paper
        className={!disabled ? 'g-hover-style' : ''}
        p={5}
        withBorder
        onClick={() => onResourceClick && onResourceClick(resource)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        h={40}
        {...paperProps}>
        <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
          <Flex direction="row" align="center" w="100%" maw={`calc(100% - 300px)`}>
            {canSelect && (
              <Checkbox
                mx={5}
                onChange={() => canSelect && onResourceClick && onResourceClick(resource)}
                size="xs"
                checked={selected}
              />
            )}
            <Space w={5} />
            <Box style={{ flexShrink: 0, width: 18, height: 18 }}>{getResourceIcon(resource.type, 18, 18)}</Box>
            <Space w={5} />
            <Box w={fileNameMaxWidth ?? '100%'}>
              <Text truncate>
                {formatResourceName(resource.fileName, resource.type, !!resource?.website?.subLinks)}
              </Text>
            </Box>
          </Flex>

          <Flex direction="row" align="center">
            {newBadge ? (
              <Badge color="teal" mx={5} tt="capitalize">
                {t('APP.ACTIONS.NEW')}
              </Badge>
            ) : (
              <Flex direction="row" align="center">
                {isUplodedResource(resource) && isSyncEnabled(resource) && subscription.product.enableSync && (
                  <Tooltip
                    position="top"
                    mx={5}
                    label={
                      <>
                        <Text>
                          {`${t('RESOURCE_TILE.SYNC.TOOLTIP_LAST_UPDATED', {
                            lastUpdated: formatDistanceToNow(new Date(resource.lastSync ?? resource.createdAt), {
                              addSuffix: true,
                              locale: languageToDateFnsLocale[LanguageService.getCurrentLanguage()]
                            })
                          })}`}
                        </Text>
                        <Text>
                          {`${t('RESOURCE_TILE.SYNC.TOOLTIP_SYNC_FREQUENCY', {
                            syncFrequency: resource.syncFrequencyMs
                              ? formatMillisecondsToDuration(resource.syncFrequencyMs)
                              : t('RESOURCE_TILE.SYNC.TOOLTIP_SYNC_FREQUENCY_NOT_SET')
                          })}`}
                        </Text>
                      </>
                    }>
                    <ActionIcon
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        setSyncFrequencyModalOpen(true);
                      }}>
                      <IconClockCheck size={20} />
                    </ActionIcon>
                  </Tooltip>
                )}

                <ItemTagsView tags={isUplodedResource(resource) ? resource.tags : undefined} />

                {isUplodedResource(resource) && (
                  <Box mx={5} style={{ width: 20, height: 20 }}>
                    {getResourceAiProcessingStatusIndicator(resource)}
                  </Box>
                )}

                {optionsEnabled && isUplodedResource(resource) && (
                  <Menu position="right-start" withArrow arrowPosition="center" offset={0}>
                    <Menu.Target>
                      <ActionIcon onClick={(event) => event.stopPropagation()}>
                        <IconDotsVertical />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        icon={<IconFileText />}
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          openRawTextModal();
                        }}>
                        {t('RESOURCE_VIEWER_WRAPPER.VIEW_RAW_TEXT')}
                      </Menu.Item>

                      {UserService.isCustomOrganisationMember() &&
                        UserService.hasUserAccessWithMinRole(UserAccess.ADMIN) && (
                          <Menu.Item
                            icon={<IconUsersGroup />}
                            onClick={(event) => {
                              event.stopPropagation();
                              openTeamAccesModal();
                            }}>
                            {t('APP.ACTIONS.MANAGE_TEAM_ACCESS')}
                          </Menu.Item>
                        )}

                      {isSyncResourceType(resource.type) && subscription.product.enableSync && (
                        <>
                          <Menu.Item
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              setSyncFrequencyModalOpen(true);
                            }}
                            icon={<IconClock />}>
                            {t('RESOURCE_TILE.SYNC.MENU_ITEM_SYNC_STATUS')}
                          </Menu.Item>
                        </>
                      )}

                      {isSyncResourceType(resource.type) && subscription.product.enableSync && (
                        <Menu.Item
                          icon={<IconRefresh />}
                          onClick={(event) => {
                            event.stopPropagation();
                            syncResourceMutation.mutate(resource._id);
                          }}>
                          {t('RESOURCE_TILE.SYNC.MENU_ITEM_SYNC_NOW')}
                        </Menu.Item>
                      )}

                      <Menu.Item
                        icon={<IconTag />}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditTagsModal();
                        }}>
                        {t('TAG.EDIT.TITLE')}
                      </Menu.Item>
                      <Menu.Item
                        icon={<IconTrash />}
                        color="red"
                        disabled={
                          resource.aiProcessingStatus === ResourceAiProcessingStatus.IN_PROGRESS ||
                          resource.creator !== loggedInUser._id
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteResourceMutation.mutate(resource._id);
                        }}>
                        {t('APP.ACTIONS.DELETE')}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Paper>

      {isUplodedResource(resource) && (
        <>
          <Modal opened={fileDropzoneOpened} withCloseButton={false} onClose={() => closeFileDropzone()}>
            {<FileDropzone resourceToUpdate={resource} closeFileDropzone={closeFileDropzone}></FileDropzone>}
          </Modal>
          <SyncFrequencyEditModal
            resource={resource}
            opened={syncFrequencyModalOpen}
            closeSyncFrequencyModal={closeSyncFrequencyModal}
            selectedTeamFilter={selectedTeamFilter}
            couldSync={subscription.product.enableSync}
          />
          <TeamAccessModal
            selectedTeamFilter={selectedTeamFilter}
            teamAccessModalOpen={teamAccessModalOpen}
            closeTeamAccessModal={closeTeamAccessModal}
            resource={resource}
          />
          <TagEditModal
            tags={allTags}
            opened={editTagModalOpen}
            onClose={closeEditTagsModal}
            resource={resource}
            onEditDone={() => queryClient.invalidateQueries([RESOURCES_QUERY_KEY, selectedTeamFilter])}
          />
          <RawResourceTextModal
            resourceId={resource._id}
            opened={rawTextModalOpened}
            onClose={closeRawTextModal}
            title={resource.fileName || t('RESOURCE_VIEWER_WRAPPER.RAW_TEXT_CONTENT')}
          />
        </>
      )}

      <ResourceConflictErrorModal
        errorModalOpened={errorModalOpened}
        conflictedSmartAssistants={conflictedSmartAssistants}
        onClose={onErrorModalClose}
      />
    </>
  );
}
